// Zustand Store for Trading State Management

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  AppState, 
  TradingConfig, 
  KalshiMarket,
  DEFAULT_CONFIG,
  STRATEGY_A_PARAMS,
  STRATEGY_B_PARAMS,
} from './types';
import {
  initializeStrategyState,
  addTradeToStrategy,
  updateStrategyStateWithResult,
  filterQualifyingMarkets,
  createStrategyATrade,
  createStrategyBTrade,
} from './strategies';
import { fetchAllOpenMarkets } from './kalshi-api';

// =============================================
// HELPER FUNCTIONS FOR DATE FILTERING
// =============================================

// Get market expiration date (when event resolves and pays out)
export function getMarketExpirationDate(market: KalshiMarket): Date | null {
  const dateString = market.expiration_time 
    || market.latest_expiration_time 
    || market.close_time;
  
  if (!dateString) return null;
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

// Calculate days until market expires
export function getDaysUntilExpiration(market: KalshiMarket): number | null {
  const expDate = getMarketExpirationDate(market);
  if (!expDate) return null;
  
  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

interface TradingStore extends AppState {
  // Actions
  setConfig: (config: Partial<TradingConfig>) => void;
  setBankroll: (strategy: 'A' | 'B', amount: number) => void;
  startTrading: () => void;
  stopTrading: () => void;
  toggleStrategy: (strategy: 'A' | 'B') => void;
  refreshMarkets: () => Promise<void>;
  executeTrade: (strategy: 'A' | 'B', market: KalshiMarket) => void;
  resolveTrade: (tradeId: string, result: 'yes' | 'no') => void;
  resetStrategy: (strategy: 'A' | 'B') => void;
  setError: (error: string | null) => void;
  
  // Simulation
  simulateResolution: (tradeId: string) => void;
}

const initialStrategyA = initializeStrategyState(
  STRATEGY_A_PARAMS.name,
  'USL overconfidence signal + moderate volume → bet AGAINST market lean',
  DEFAULT_CONFIG.strategyABankroll,
  DEFAULT_CONFIG.betSizePercent
);

const initialStrategyB = initializeStrategyState(
  STRATEGY_B_PARAMS.name,
  'Price 20-40¢ + moderate volume → bet WITH market lean',
  DEFAULT_CONFIG.strategyBBankroll,
  DEFAULT_CONFIG.betSizePercent
);

export const useTradingStore = create<TradingStore>()(
  persist(
    (set, get) => ({
      // Initial state
      config: DEFAULT_CONFIG,
      strategyA: initialStrategyA,
      strategyB: initialStrategyB,
      markets: [],
      qualifyingMarketsA: [],
      qualifyingMarketsB: [],
      isRunning: false,
      lastUpdate: null,
      error: null,

      // Actions
      setConfig: (newConfig) => set((state) => ({
        config: { ...state.config, ...newConfig }
      })),

      setBankroll: (strategy, amount) => set((state) => {
        if (strategy === 'A') {
          return {
            strategyA: {
              ...state.strategyA,
              bankroll: amount,
              initialBankroll: state.strategyA.trades.length === 0 ? amount : state.strategyA.initialBankroll,
            },
            config: { ...state.config, strategyABankroll: amount }
          };
        } else {
          return {
            strategyB: {
              ...state.strategyB,
              bankroll: amount,
              initialBankroll: state.strategyB.trades.length === 0 ? amount : state.strategyB.initialBankroll,
            },
            config: { ...state.config, strategyBBankroll: amount }
          };
        }
      }),

      startTrading: () => {
        console.log('startTrading called');
        set({ isRunning: true });
        console.log('isRunning set to true');
      },
      
      stopTrading: () => {
        console.log('stopTrading called');
        set({ isRunning: false });
        console.log('isRunning set to false');
      },

      toggleStrategy: (strategy) => set((state) => {
        if (strategy === 'A') {
          return {
            config: { ...state.config, strategyAEnabled: !state.config.strategyAEnabled }
          };
        } else {
          return {
            config: { ...state.config, strategyBEnabled: !state.config.strategyBEnabled }
          };
        }
      }),

      refreshMarkets: async () => {
        try {
          set({ error: null });
          
          // Fetch more pages (50 pages = up to 10000 markets)
          // Note: Kalshi returns furthest-first, so we need all pages to get short-term markets
          const markets = await fetchAllOpenMarkets(50);
          
          const maxDays = get().config.maxDaysToClose;
          
          // ========== DEBUG: Analyze date fields ==========
          console.log('🔍 ===== DATE FIELD ANALYSIS =====');
          
          // Count markets by date field availability
          const hasExpiration = markets.filter(m => m.expiration_time).length;
          const hasCloseTime = markets.filter(m => m.close_time).length;
          const hasLatestExp = markets.filter(m => m.latest_expiration_time).length;
          const hasAnyDate = markets.filter(m => m.expiration_time || m.close_time || m.latest_expiration_time).length;
          const noDate = markets.filter(m => !m.expiration_time && !m.close_time && !m.latest_expiration_time).length;
          
          console.log(`📊 Total markets: ${markets.length}`);
          console.log(`📊 Has expiration_time: ${hasExpiration}`);
          console.log(`📊 Has close_time: ${hasCloseTime}`);
          console.log(`📊 Has latest_expiration_time: ${hasLatestExp}`);
          console.log(`📊 Has ANY date field: ${hasAnyDate}`);
          console.log(`📊 NO date field: ${noDate}`);
          
          // Sample some markets to see their structure
          console.log('🔍 ===== SAMPLE MARKETS (first 5) =====');
          markets.slice(0, 5).forEach((m, i) => {
            const expDate = getMarketExpirationDate(m);
            const daysUntil = expDate ? getDaysUntilExpiration(m) : null;
            console.log(`Market ${i + 1}:`, {
              ticker: m.ticker,
              title: m.title?.slice(0, 40),
              price: m.last_price,
              volume: m.volume,
              expiration_time: m.expiration_time,
              close_time: m.close_time,
              latest_expiration_time: m.latest_expiration_time,
              parsedDate: expDate?.toISOString(),
              daysUntil: daysUntil,
            });
          });
          
          // Analyze date distribution
          const now = new Date();
          const dateStats = {
            noDate: 0,
            expired: 0,
            within7: 0,
            within30: 0,
            within90: 0,
            within180: 0,
            beyond180: 0,
          };
          
          markets.forEach(m => {
            const daysUntil = getDaysUntilExpiration(m);
            if (daysUntil === null) {
              dateStats.noDate++;
            } else if (daysUntil < 0) {
              dateStats.expired++;
            } else if (daysUntil <= 7) {
              dateStats.within7++;
            } else if (daysUntil <= 30) {
              dateStats.within30++;
            } else if (daysUntil <= 90) {
              dateStats.within90++;
            } else if (daysUntil <= 180) {
              dateStats.within180++;
            } else {
              dateStats.beyond180++;
            }
          });
          
          console.log('🔍 ===== DATE DISTRIBUTION =====');
          console.log(`No date: ${dateStats.noDate}`);
          console.log(`Expired: ${dateStats.expired}`);
          console.log(`Within 7 days: ${dateStats.within7}`);
          console.log(`Within 30 days: ${dateStats.within30}`);
          console.log(`Within 90 days: ${dateStats.within90}`);
          console.log(`Within 180 days: ${dateStats.within180}`);
          console.log(`Beyond 180 days: ${dateStats.beyond180}`);
          
          // Find markets expiring within 7 days
          const within7Days = markets.filter(m => {
            const expDate = getMarketExpirationDate(m);
            if (!expDate) return false;
            const days = (expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
            return days >= 0 && days <= 7;
          });
          
          console.log(`🔍 ===== MARKETS WITHIN 7 DAYS: ${within7Days.length} =====`);
          within7Days.slice(0, 10).forEach((m, i) => {
            const expDate = getMarketExpirationDate(m);
            const days = expDate ? Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : 'N/A';
            console.log(`${i + 1}. ${m.ticker} | ${m.last_price}¢ | vol:${m.volume} | expires in ${days} days`);
          });
          
          // Find Strategy B qualifying markets within 7 days
          const stratBWithin7 = within7Days.filter(m => 
            m.last_price >= 20 && 
            m.last_price <= 40 && 
            m.volume >= 100 && 
            m.volume <= 1000
          );
          console.log(`🔍 ===== STRATEGY B WITHIN 7 DAYS: ${stratBWithin7.length} =====`);
          stratBWithin7.forEach((m, i) => {
            console.log(`${i + 1}. ${m.ticker} | ${m.last_price}¢ | vol:${m.volume}`);
          });
          
          // ========== END DEBUG ==========
          
          // Filter by expiration date - BUT include markets with unknown dates
          const filteredByDate = markets.filter(m => {
            // If showing all time, include everything
            if (maxDays >= 9999) return true;
            
            const daysUntil = getDaysUntilExpiration(m);
            
            // IMPORTANT: If no expiration date found, INCLUDE the market
            // This prevents accidentally filtering out sports/other markets
            if (daysUntil === null) return true;
            
            // Include if expires within maxDays (allow 1 day buffer for settling)
            return daysUntil >= -1 && daysUntil <= maxDays;
          });
          
          console.log(`📊 Date filter result: ${filteredByDate.length} markets within ${maxDays} days`);
          
          const { strategyA, strategyB } = filterQualifyingMarkets(filteredByDate);
          
          console.log(`📊 Qualifying after date filter: A=${strategyA.length}, B=${strategyB.length}`);
          
          set({
            markets: filteredByDate,
            qualifyingMarketsA: strategyA,
            qualifyingMarketsB: strategyB,
            lastUpdate: new Date(),
          });
        } catch (error) {
          console.error('❌ Refresh error:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to fetch markets' });
        }
      },

      executeTrade: (strategy, market) => set((state) => {
        const strategyState = strategy === 'A' ? state.strategyA : state.strategyB;
        
        // Create the trade
        const trade = strategy === 'A' 
          ? createStrategyATrade(market, strategyState.bankroll, state.config.betSizePercent)
          : createStrategyBTrade(market, strategyState.bankroll, state.config.betSizePercent);
        
        // Check if we can afford it
        if (trade.cost > strategyState.bankroll) {
          return { error: `Insufficient bankroll for ${strategy === 'A' ? 'Strategy A' : 'Strategy B'}` };
        }
        
        // Deduct cost from bankroll
        const updatedState = addTradeToStrategy(strategyState, trade);
        updatedState.bankroll -= trade.cost;
        
        if (strategy === 'A') {
          return { strategyA: updatedState };
        } else {
          return { strategyB: updatedState };
        }
      }),

      resolveTrade: (tradeId, result) => set((state) => {
        // Find which strategy has this trade
        const tradeA = state.strategyA.trades.find(t => t.id === tradeId);
        const tradeB = state.strategyB.trades.find(t => t.id === tradeId);
        
        if (tradeA) {
          return { strategyA: updateStrategyStateWithResult(state.strategyA, tradeId, result) };
        } else if (tradeB) {
          return { strategyB: updateStrategyStateWithResult(state.strategyB, tradeId, result) };
        }
        return {};
      }),

      resetStrategy: (strategy) => set((state) => {
        if (strategy === 'A') {
          return {
            strategyA: initializeStrategyState(
              STRATEGY_A_PARAMS.name,
              'USL overconfidence signal + moderate volume → bet AGAINST market lean',
              state.config.strategyABankroll,
              state.config.betSizePercent
            )
          };
        } else {
          return {
            strategyB: initializeStrategyState(
              STRATEGY_B_PARAMS.name,
              'Price 20-40¢ + moderate volume → bet WITH market lean',
              state.config.strategyBBankroll,
              state.config.betSizePercent
            )
          };
        }
      }),

      setError: (error) => set({ error }),

      // Simulation: randomly resolve a trade based on historical win rates
      simulateResolution: (tradeId) => {
        const state = get();
        const tradeA = state.strategyA.trades.find(t => t.id === tradeId);
        const tradeB = state.strategyB.trades.find(t => t.id === tradeId);
        
        const trade = tradeA || tradeB;
        if (!trade || trade.result !== 'pending') return;
        
        // Historical win rates from backtest
        // Strategy A: 10% win rate (betting on longshots)
        // Strategy B: 86% win rate (betting with market)
        const winRate = tradeA ? 0.10 : 0.86;
        const won = Math.random() < winRate;
        
        // Determine the market result based on our bet and whether we won
        // If we bet YES and won, result is 'yes'
        // If we bet NO and won, result is 'no'
        // If we bet YES and lost, result is 'no'
        // If we bet NO and lost, result is 'yes'
        const result = (trade.betDirection === 'YES') === won ? 'yes' : 'no';
        
        get().resolveTrade(tradeId, result);
      },
    }),
    {
      name: 'kalshi-usl-trader',
      partialize: (state) => ({
        config: state.config,
        strategyA: state.strategyA,
        strategyB: state.strategyB,
      }),
    }
  )
);

// Selector hooks for performance
export const useConfig = () => useTradingStore((state) => state.config);
export const useStrategyA = () => useTradingStore((state) => state.strategyA);
export const useStrategyB = () => useTradingStore((state) => state.strategyB);
export const useMarkets = () => useTradingStore((state) => state.markets);
export const useIsRunning = () => useTradingStore((state) => state.isRunning);
